import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAuthToken } from "../../api/auth";
import { submitCleanedDirect } from "../../api/reports";
import { uploadImagesDirect, getUploadErrorMessage } from "../../api/uploads";
import { useDataContext } from "../../contexts/DataContext";
import "../styles/CleanupPage.css";

interface CleanupPageProps {
  reportId: string;
  files: File[];
  useAdminImage?: boolean;
  adminImageVariant?: "not_found";
  onClose: () => void;
  onSuccess: () => void;
}

const getCleanerTypes = (t: any) => [
  { value: 0, label: t("cleanup.individual") },
  { value: 1, label: t("cleanup.group") },
  { value: 2, label: t("cleanup.administration") },
];

const CleanupPage: React.FC<CleanupPageProps> = ({
  reportId,
  files,
  useAdminImage = false,
  adminImageVariant,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { updateData, loadReportDetails } = useDataContext();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [amount, setAmount] = useState<number>(useAdminImage ? 0 : 1);
  const [cleaner, setCleaner] = useState<number>(useAdminImage ? 2 : 0);
  const [agreeToRules, setAgreeToRules] = useState(true);

  // Create preview URLs for images
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  useEffect(() => {
    if (!useAdminImage && files.length > 0) {
      const previews = files.map((file) => URL.createObjectURL(file));
      setImagePreviews(previews);

      // Cleanup blob URLs on unmount
      return () => {
        previews.forEach((url) => URL.revokeObjectURL(url));
      };
    }
  }, [files, useAdminImage]);

  const handleCleanerChange = useCallback(
    (value: number) => {
      setCleaner(value);
      if (value === 2) {
        setAmount(0);
      } else if (amount === 0) {
        setAmount(1);
      }
    },
    [amount]
  );

  const handleSubmit = async () => {
    if (loading || !agreeToRules) return;

    const token = getAuthToken();
    if (!token) {
      alert(t("auth.authRequired"));
      return;
    }

    setLoading(true);
    try {
      // Images upload straight to GCS; we submit only their keys (admin
      // placeholder cleanups carry no images at all).
      const imageKeys = useAdminImage
        ? []
        : await uploadImagesDirect(files, token);

      await submitCleanedDirect(
        {
          _id: reportId,
          comment,
          amount,
          cleaner,
          ...(useAdminImage
            ? {
                useAdminImage: true,
                ...(adminImageVariant ? { adminImageType: adminImageVariant } : {}),
              }
            : { imageKeys }),
        },
        token,
      );

      await updateData();
      // Refresh this report's full data so the new cleanup propagates.
      await loadReportDetails(reportId, true);
      alert(t("cleanup.cleanupSubmitted"));
      onSuccess();
    } catch (error) {
      alert(getUploadErrorMessage(error, t));
    } finally {
      setLoading(false);
    }
  };

  const getTrashIcon = (value: number): string => {
    if (value < 10) return `${process.env.PUBLIC_URL}/images/trash0.png`;
    if (value < 20) return `${process.env.PUBLIC_URL}/images/trash1.png`;
    return `${process.env.PUBLIC_URL}/images/trash2.png`;
  };

  const CLEANER_TYPES = getCleanerTypes(t);

  return (
    <>
      <div className="cleanup-page-overlay" onClick={onClose} />
      <div className="cleanup-page">
        <div className="cleanup-page-header">
          <h2>{t("cleanup.markAsCleaned")}</h2>
          <button className="cleanup-page-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="cleanup-page-content">
          {/* Images Section */}
          <div className="cleanup-images-section">
            {useAdminImage ? (
              <img
                src={`${process.env.PUBLIC_URL}/images/${
                  adminImageVariant === "not_found"
                    ? "adm_not_found.png"
                    : "adm_clean.png"
                }`}
                alt="Admin cleanup"
                className="cleanup-image-preview"
              />
            ) : imagePreviews.length > 0 ? (
              <div className="cleanup-images-grid">
                {imagePreviews.map((preview, index) => (
                  <img
                    key={index}
                    src={preview}
                    alt={`Cleanup ${index + 1}`}
                    className="cleanup-image-preview"
                  />
                ))}
              </div>
            ) : (
              <div className="no-images">{t("cleanup.noImages")}</div>
            )}
          </div>

          {/* Comment Section */}
          <div className="cleanup-section">
            <label className="cleanup-label">{t("report.commentOptional")}</label>
            <textarea
              className="cleanup-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("report.addCommentPlaceholder")}
              maxLength={300}
              rows={3}
            />
          </div>

          {/* Cleaned By Section */}
          <div className="cleanup-section">
            <label className="cleanup-label">{t("cleanup.cleanedBy")}</label>
            <div className="cleaner-options">
              {CLEANER_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={`cleaner-option ${
                    cleaner === type.value ? "active" : ""
                  }`}
                  onClick={() => handleCleanerChange(type.value)}
                  disabled={loading}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cleanup Amount Section (hidden for Administration) */}
          {cleaner !== 2 && (
            <div className="cleanup-section">
              <label className="cleanup-label">{t("cleanup.cleanupAmount")}</label>
              <div className="amount-display">
                <img
                  src={getTrashIcon(amount)}
                  alt="Amount"
                  className="amount-icon"
                />
                <span className="amount-value">{amount}</span>
              </div>
              <input
                type="range"
                min="1"
                max="40"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="amount-slider"
                disabled={loading}
              />
            </div>
          )}

          {/* Rules Agreement */}
          <div className="cleanup-section">
            <label className="rules-checkbox">
              <input
                type="checkbox"
                checked={agreeToRules}
                onChange={(e) => setAgreeToRules(e.target.checked)}
                disabled={loading}
              />
                <span>
                {t("rules.agreeToRules")}{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rules-link"
                >
                  {t("rules.rules")}
                </a>
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            className="cleanup-submit-btn"
            onClick={handleSubmit}
            disabled={loading || !agreeToRules}
          >
            {loading ? t("report.submitting") : t("cleanup.submitCleanup")}
          </button>
        </div>
      </div>
    </>
  );
};

export default CleanupPage;
